import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FileText, MessageSquare, Shield, PenTool, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user) {
      if (user.role === 'admin' || user.role === 'sub_admin' || user.role === 'super_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/client/dashboard');
      }
    }
  }, [user, navigate]);

  const portalFeatures = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Secure Documents',
      description: 'Access and upload tax documents securely'
    },
    {
      icon: <PenTool className="w-6 h-6" />,
      title: 'E-Signatures',
      description: 'Sign engagement letters and forms digitally'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Direct Messaging',
      description: 'Communicate directly with our team'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Bank-Level Security',
      description: 'Your data is protected with encryption'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="/ppmt-logo.png" 
                alt="PPMT Strategic Group" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <div className="flex items-center space-x-3">
              <Button
                data-testid="login-button"
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-6"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Welcome to Your
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent"> Client Portal</span>
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Access your documents, sign forms, and communicate with the PPMT Strategic Group team — all in one secure place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              data-testid="hero-login-button"
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-6 text-lg rounded-full"
            >
              Access My Portal
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {portalFeatures.map((feature, index) => (
              <div
                key={index}
                className="glass rounded-xl p-5 text-center transition-all duration-300 hover:shadow-lg animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center mx-auto mb-3 text-teal-600">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 text-sm">{feature.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="glass rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Need Help?</h2>
            <p className="text-gray-600 mb-6">
              If you're having trouble accessing your portal or have questions about your account, our team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-sm">
              <a 
                href="tel:+1234567890" 
                className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors"
              >
                <Phone className="w-4 h-4" />
                (646) 289-5358
              </a>
              <span className="hidden sm:inline text-gray-300">|</span>
              <a 
                href="mailto:support@ppmtgroup.com" 
                className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
                support@ppmtgroup.com
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 px-4 sm:px-6 lg:px-8 mt-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <img 
                src="/ppmt-logo.png" 
                alt="PPMT Strategic Group" 
                className="h-8 w-auto object-contain mx-auto md:mx-0 mb-2 brightness-0 invert opacity-80"
              />
              <p className="text-gray-400 text-sm">Your Trusted Tax & Accounting Partner</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>123 Business Ave, Suite 100</span>
              </div>
              <p>© 2025 PPMT Strategic Group. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
